import { useState } from "react";
import { Banner, Button, Input, Label, Modal, Textarea } from "@panelgenie/shared-ui";

/**
 * TC-6 — curate a regression test case from a real invocation.
 *
 * Replaces the old "Export cassette" download (a developer-local replay
 * pin) with a durable, server-side corpus entry: the staff member names
 * the case and jots what's special / what to watch, and `createTestCase`
 * copies the invocation's photos into the durable corpus prefix and
 * freezes the baseline response. On success the caller navigates to the
 * new case's detail page (`onSaved(id)`).
 *
 * `submit` is injected (it wraps `support.createTestCase`) so the form is
 * unit-testable without a tRPC client / RouterProvider; the route wires
 * the real mutation + navigation.
 */
export type SaveTestCaseSubmit = (input: {
  readonly invocationId: string;
  readonly name: string;
  readonly notes: string;
}) => Promise<{ readonly id: string }>;

export function SaveTestCaseModal({
  invocationId,
  defaultName,
  submit,
  onSaved,
  onClose,
}: {
  readonly invocationId: string;
  readonly defaultName?: string;
  readonly submit: SaveTestCaseSubmit;
  readonly onSaved: (id: string) => void;
  readonly onClose: () => void;
}) {
  const [name, setName] = useState(defaultName ?? "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !saving;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    void submit({ invocationId, name: trimmedName, notes: notes.trim() })
      .then((result) => {
        onSaved(result.id);
      })
      .catch((cause: unknown) => {
        setSaving(false);
        setError(
          cause instanceof Error
            ? cause.message
            : "Couldn't save the test case. Please try again.",
        );
      });
  };

  return (
    <Modal
      open
      onClose={onClose}
      aria-label="save-test-case"
      title="Save as test case"
      className="max-w-lg"
      footer={
        <>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-label="save-test-case-submit"
          >
            {saving ? "Saving…" : "Save test case"}
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-mute">
          Freeze this scan as a durable regression case: its photos are copied
          into the corpus and the model response is captured as the baseline to
          re-run process changes against.
        </p>

        {error !== null ? (
          <Banner variant="danger" title="Couldn't save">
            <span className="text-sm">{error}</span>
          </Banner>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="test-case-name" required>
            Name
          </Label>
          <Input
            id="test-case-name"
            aria-label="test-case-name"
            value={name}
            placeholder="e.g. Square D tandem column, faded legend"
            invalid={error !== null}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="test-case-notes">Notes</Label>
          <Textarea
            id="test-case-notes"
            aria-label="test-case-notes"
            value={notes}
            rows={4}
            placeholder="What's unusual about this panel, and what should a re-run watch for?"
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}
